/**
 * The single API instance. Both apps inject their endpoints into it.
 *
 * One instance rather than one per app because the base query, the refresh handling and the tag
 * vocabulary must be identical — two copies would drift, and the copy that drifts is the one
 * running at the counter.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery, type SessionEnded } from './baseQuery.js';
import { TAGS } from './tags.js';

let baseUrl = '/api/v1';
let sessionEnded: SessionEnded = () => {
  // Replaced during store setup. The default is a no-op rather than a throw so an early failure
  // surfaces as a 401 in the UI, not as an unhandled rejection in the console.
};
let needsUnlock: () => void = () => {
  // Replaced during store setup, POS only. A no-op default is safe for the dashboard, which
  // never triggers it — see `recoverSession` in `baseQuery.ts`.
};

/**
 * Which app this bundle is. The refresh cookie is shared across every subdomain, so the server
 * cannot tell POS and dashboard sessions apart from the cookie alone — this is stamped onto
 * `POST /auth/login` so the device row it creates can be. There is no safe default: an app that
 * forgets to configure this would otherwise silently claim to be one specific channel.
 */
let channel: 'pos' | 'dashboard' | null = null;

/** Called once, before the store is created. */
export function configureApi(options: {
  baseUrl?: string;
  channel?: 'pos' | 'dashboard';
  onSessionEnded?: SessionEnded;
  /** POS only — see `needsUnlock`'s doc comment. */
  onNeedsUnlock?: () => void;
}): void {
  if (options.baseUrl !== undefined) baseUrl = options.baseUrl.replace(/\/$/, '');
  if (options.channel !== undefined) channel = options.channel;
  if (options.onSessionEnded !== undefined) sessionEnded = options.onSessionEnded;
  if (options.onNeedsUnlock !== undefined) needsUnlock = options.onNeedsUnlock;
}

export function getChannel(): 'pos' | 'dashboard' | null {
  return channel;
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: createBaseQuery({
    baseUrl: () => baseUrl,
    channel: () => channel,
    onSessionEnded: (code, message) => {
      sessionEnded(code, message);
    },
    onNeedsUnlock: () => {
      needsUnlock();
    },
  }),
  tagTypes: TAGS,
  // Kept modest on purpose: the POS runs on a phone that may be open for a whole shift, and an
  // unbounded cache of sale lists is memory the browser eventually reclaims by killing the tab.
  keepUnusedDataFor: 120,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});

/** Everything the app should reset when the counter changes hands. */
export const resetApiState = api.util.resetApiState;
